import * as React from 'react';

import { format, parse } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/TailwindComponents/base/Select';
import { cn } from '@/components/TailwindComponents/lib/utils';

interface MonthYearPickerProps {
  value?: string | Date; // MM/yyyy format string or Date object
  onSelect?: (value: string | Date | undefined) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  showClearBtn?: boolean;
  yearsBack?: number; // Number of years to go back from current year (default: 100 for birth dates)
  maxAge?: number; // Maximum age for birth date validation (default: 120)
  minAge?: number; // Minimum age for birth date validation (default: 0)
}

export function MonthYearPicker({
  value,
  onSelect,
  className,
  disabled,
  placeholder = 'Select month and year',
  showClearBtn = false,
  yearsBack = 100,
  maxAge,
  minAge,
}: MonthYearPickerProps) {
  // Determine if value is string or Date
  const isStringValue = typeof value === 'string';

  // Convert value to Date for internal use
  const currentDate = React.useMemo(() => {
    if (!value) return undefined;

    if (isStringValue) {
      try {
        return parse(value as string, 'MM/yyyy', new Date());
      } catch {
        return undefined;
      }
    }
    return value as Date;
  }, [value, isStringValue]);

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    currentDate,
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempMonth, setTempMonth] = React.useState<number>(
    currentDate?.getMonth() ?? new Date().getMonth(),
  );
  const [tempYear, setTempYear] = React.useState<number>(
    currentDate?.getFullYear() ?? new Date().getFullYear(),
  );
  const wasCancelled = React.useRef<boolean>(false);

  const months = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const dt = new Date(2000, i);
      return {
        value: i,
        label: format(dt, 'MMMM'),
      };
    });
  }, []);

  const years = React.useMemo(() => {
    const yearNow = new Date().getFullYear();
    return Array.from(
      {
        length:
          minAge && maxAge ? 1 + yearsBack : 1 + yearsBack + yearsBack / 2,
      },
      (_, i) => yearNow - yearsBack + i,
    );
  }, [yearsBack]);

  // Age validation helper
  const getAge = (month: number, year: number): number => {
    const today = new Date();
    const birthDate = new Date(year, month);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // Check if selected date is valid for age range
  const isDateValid = (month: number, year: number): boolean => {
    const age = getAge(month, year);
    return minAge && maxAge ? age >= minAge && age <= maxAge : true;
  };

  // Update temp state when date or value prop changes
  React.useEffect(() => {
    if (currentDate) {
      setSelectedDate(currentDate);
      setTempMonth(currentDate.getMonth());
      setTempYear(currentDate.getFullYear());
    }
  }, [currentDate]);

  // Handle clickaway logic - apply changes when picker closes unless cancelled
  React.useEffect(() => {
    if (!isOpen) {
      if (!wasCancelled.current) {
        const hasDateChanged =
          tempMonth !== selectedDate?.getMonth() ||
          tempYear !== selectedDate?.getFullYear();

        if (hasDateChanged) {
          const newDate = new Date(tempYear, tempMonth);
          setSelectedDate(newDate);

          // Return value in same format as input
          if (isStringValue) {
            const stringValue = format(newDate, 'MM/yyyy');
            onSelect?.(stringValue);
          } else {
            onSelect?.(newDate);
          }
        }
      }
      // Reset cancellation flag
      wasCancelled.current = false;
    }
  }, [isOpen, tempMonth, tempYear, selectedDate, onSelect, isStringValue]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Reset temp state to current values when opening
      setTempMonth(selectedDate?.getMonth() ?? new Date().getMonth());
      setTempYear(selectedDate?.getFullYear() ?? new Date().getFullYear());
      wasCancelled.current = false;
    }
    setIsOpen(open);
  };

  const handleSelect = () => {
    const newDate = new Date(tempYear, tempMonth);
    setSelectedDate(newDate);

    // Return value in same format as input
    if (isStringValue) {
      const stringValue = format(newDate, 'MM/yyyy');
      onSelect?.(stringValue);
    } else {
      onSelect?.(newDate);
    }
    setIsOpen(false);
  };

  const isValidSelection = isDateValid(tempMonth, tempYear);

  const handleCancel = () => {
    wasCancelled.current = true;
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedDate(undefined);
    onSelect?.(undefined);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-[280px] justify-start text-left font-normal',
            !selectedDate && 'text-muted-foreground',
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, 'MMMM yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-[280px] w-auto p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Month</label>
              <Select
                value={tempMonth.toString()}
                onValueChange={(monthValue) => setTempMonth(Number(monthValue))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem
                      key={month.value}
                      value={month.value.toString()}
                    >
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Year</label>
              <Select
                value={tempYear.toString()}
                onValueChange={(yearValue) => setTempYear(Number(yearValue))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              {showClearBtn && (
                <Button variant="ghost" size="sm" onClick={handleClear}>
                  Clear
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
            <Button
              size="sm"
              onClick={handleSelect}
              disabled={!isValidSelection}
              className={
                !isValidSelection ? 'opacity-50 cursor-not-allowed' : ''
              }
            >
              Select
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
